import { useEffect } from 'react'

import {
  EEmbeddedChatMessageType,
  TEmbeddedChatMessage,
} from '~/features/chatbot/models'
import { isValidParentMessage } from '~/features/chatbot/utils'
import { store } from '~/store'

// Registers the postMessage listener for parent-page context (referrer,
// current page) once, at layout level — called from ChatBotLayout, which
// mounts before useEmbeddedIconStyles sends the loadIconStyles request that
// triggers the parent's one-time updateReferrer/updateCurrentPage response,
// and which stays mounted across LinkView<->Simple theme switches and
// Simple's minimize/reopen. Writing into the chatbot store (rather than local
// component state, which is where this used to live — inside useChatSource,
// only active while DialogInput was mounted) is what makes the values survive
// those transitions instead of being missed or reset to empty.
const useEmbeddedChatContext = () => {
  const setChatbot = store((state) => state.setChatbot)

  useEffect(() => {
    // Seed from document.referrer in case the parent embed script is an older
    // version that doesn't send updateReferrer.
    try {
      const referrerUrl =
        typeof document !== 'undefined' && document.referrer
          ? new URL(document.referrer).origin
          : ''

      if (referrerUrl) {
        setChatbot({ referrer: referrerUrl })
      }
    } catch {
      // ignore malformed document.referrer
    }

    const handleMessage = (event: MessageEvent) => {
      if (!event.origin || typeof event.origin !== 'string') {
        return
      }

      if (event.origin === window.location.origin) {
        return
      }

      if (!isValidParentMessage(event.data)) {
        return
      }

      const message = event.data as TEmbeddedChatMessage

      if (message.type === EEmbeddedChatMessageType.updateReferrer) {
        const { referrer } = message.data || {}

        if (referrer && typeof referrer === 'string') {
          setChatbot({ referrer })
        }
        return
      }

      if (message.type === EEmbeddedChatMessageType.updateCurrentPage) {
        const { currentPage } = message.data || {}

        if (currentPage && typeof currentPage === 'string') {
          setChatbot({ currentPage })
        }
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [setChatbot])
}

export default useEmbeddedChatContext
